import {useState} from 'react';
import {localStorageKeysConstants} from '../../constants';
import {useIsFeaturesLoaded} from '../feature-flags/useIsFeaturesLoaded';
import {useIsSdiEnabled} from '../feature-flags/useIsSdiEnabled';

const {SDI_BANNER_DISMISSED} = localStorageKeysConstants;

/**
 * Whether to show the "connect your cassetto fiscale" prompt on the landing page.
 *
 * Mirrors `useIsMissingDetailsCardOpened`: the flag says whether the thing is already done, the
 * browser remembers whether this person has waved the prompt away, and the page renders it inside
 * an `UnmountClosed`.
 *
 * Dismissal is per-browser and deliberately not server state — this is an upsell prompt, not a step
 * in a workflow, so dismissing it on a laptop should not hide it forever on a phone.
 *
 * ⚠ Waits for the company features to have LOADED. `useCompanyFeatureAvailable` answers `false`
 * while they are still in flight, which is indistinguishable from "not connected" — so without this
 * every already-connected company would see the banner appear on each page load and then vanish a
 * moment later. Same shape as the terms-of-use flash in H1MVP-4787: a negative check answering
 * before its data exists.
 *
 * @returns {{isOpened: boolean, onClose: function}} Whether to render it, and the dismissal handler.
 *
 * @example
 * const {isOpened, onClose} = useIsCassettoFiscaleBannerOpened();
 */
export const useIsCassettoFiscaleBannerOpened = () => {
  const [dismissed, setDismissed] = useState(() => Boolean(localStorage.getItem(SDI_BANNER_DISMISSED)));
  const isFeaturesLoaded = useIsFeaturesLoaded();
  const isSdiEnabled = useIsSdiEnabled();

  const onClose = () => {
    localStorage.setItem(SDI_BANNER_DISMISSED, '1');
    setDismissed(true);
  };

  return {isOpened: isFeaturesLoaded && !isSdiEnabled && !dismissed, onClose};
};
